import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconX, IconRobot, IconSearch } from '@tabler/icons-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const AnswerSidebar = ({ isOpen, onClose, answer }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Small delay to trigger animation
      setTimeout(() => setIsVisible(true), 10);
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getEngineIcon = (engine) => {
    const engineName = engine?.toLowerCase() || '';
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };

    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img
          src={logoSrc}
          alt={`${engine} logo`}
          className="w-8 h-8 rounded-full object-cover"
        />
      );
    }
    return <IconRobot size={32} className="text-gray-400 dark:text-[#999999]" />;
  };

  const getEngineName = (engine) => {
    const engineName = engine?.toLowerCase() || '';
    if (engineName === 'openai') return 'ChatGPT';
    return engine ? engine.charAt(0).toUpperCase() + engine.slice(1) : 'Unknown';
  };

  return (
    <>
      {/* Invisible click target for closing sidebar */}
      {isVisible && (
        <div
          className="fixed inset-0 z-[9998]"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full bg-white dark:bg-[#1f1f1f] z-[9999]
          w-full md:w-[600px] lg:w-[700px] overflow-hidden
          transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : (isRTL ? '-translate-x-full' : 'translate-x-full')
        }`}
        style={{
          boxShadow: isRTL ? '20px 0 60px rgba(0, 0, 0, 0.25), 10px 0 30px rgba(0, 0, 0, 0.15)' : '-20px 0 60px rgba(0, 0, 0, 0.25), -10px 0 30px rgba(0, 0, 0, 0.15)'
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#1f1f1f] border-b border-gray-200 dark:border-[#2e2e2e] px-6 py-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {answer?.engine && getEngineIcon(answer.engine)}
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-[#f5f5f5]">
                  {getEngineName(answer?.engine)}
                </h2>
                {answer?.executedAt && (
                  <p className="text-xs text-gray-500 dark:text-[#999999] mt-0.5">
                    {new Date(answer.executedAt).toLocaleString(i18n.language === 'he' ? 'he-IL' : 'en-US')}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-[#262626] rounded-lg transition-colors"
              aria-label={t('common.buttons.close')}
            >
              <IconX size={24} className="text-gray-500 dark:text-[#999999]" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="h-full overflow-y-auto pb-20">
          <div className="px-6 py-6 space-y-6">
            {/* Query Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <IconSearch size={20} className="text-gray-400 dark:text-[#999999]" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-[#c4c4c4] uppercase tracking-wider">
                  {t('results.answerSidebar.query')}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-[#262626] rounded-lg p-4">
                <p className="text-gray-800 dark:text-[#f5f5f5] font-medium">
                  {answer?.searchTerm || t('results.answerSidebar.noQuery')}
                </p>
              </div>
            </div>

            {/* Answer Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <IconRobot size={20} className="text-gray-400 dark:text-[#999999]" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-[#c4c4c4] uppercase tracking-wider">
                  {t('results.answerSidebar.answer')}
                </h3>
              </div>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                {answer?.loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : answer?.error ? (
                  <p className="text-red-600 dark:text-red-300">{answer.error}</p>
                ) : answer?.textAnswer ? (
                  <div className="prose prose-sm max-w-none text-gray-800 dark:text-[#f5f5f5]">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Custom rendering for specific markdown elements
                        h1: ({children}) => <h1 className="text-2xl font-bold mb-4 mt-6 text-gray-900 dark:text-[#f5f5f5]">{children}</h1>,
                        h2: ({children}) => <h2 className="text-xl font-semibold mb-3 mt-5 text-gray-900 dark:text-[#f5f5f5]">{children}</h2>,
                        h3: ({children}) => <h3 className="text-lg font-semibold mb-2 mt-4 text-gray-900 dark:text-[#f5f5f5]">{children}</h3>,
                        p: ({children}) => <p className="mb-3 leading-relaxed">{children}</p>,
                        ul: ({children}) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                        li: ({children}) => <li className="ml-2">{children}</li>,
                        strong: ({children}) => <strong className="font-semibold text-gray-900 dark:text-[#f5f5f5]">{children}</strong>,
                        code: ({inline, children}) =>
                          inline ? (
                            <code className="px-1 py-0.5 bg-gray-100 dark:bg-[#262626] rounded text-sm font-mono">{children}</code>
                          ) : (
                            <code className="block p-3 bg-gray-100 dark:bg-[#262626] rounded text-sm font-mono overflow-x-auto">{children}</code>
                          ),
                        blockquote: ({children}) => (
                          <blockquote className="border-l-4 border-gray-300 dark:border-[#2e2e2e] pl-4 italic my-3">{children}</blockquote>
                        ),
                        a: ({href, children}) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-300 hover:underline">
                            {children}
                          </a>
                        ),
                        // Table components
                        table: ({children}) => (
                          <div className="overflow-x-auto my-4">
                            <table className="min-w-full border-collapse border border-gray-300 dark:border-[#2e2e2e]">{children}</table>
                          </div>
                        ),
                        thead: ({children}) => <thead className="bg-gray-100 dark:bg-[#262626]">{children}</thead>,
                        tbody: ({children}) => <tbody>{children}</tbody>,
                        tr: ({children}) => <tr className="border-b border-gray-300 dark:border-[#2e2e2e]">{children}</tr>,
                        th: ({children}) => (
                          <th className="border border-gray-300 dark:border-[#2e2e2e] px-3 py-2 text-left text-sm font-semibold text-gray-700 dark:text-[#c4c4c4]">
                            {children}
                          </th>
                        ),
                        td: ({children}) => (
                          <td className="border border-gray-300 dark:border-[#2e2e2e] px-3 py-2 text-sm text-gray-700 dark:text-[#c4c4c4]">{children}</td>
                        )
                      }}
                    >
                      {answer.textAnswer}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-[#999999] italic">{t('results.answerSidebar.noAnswer')}</p>
                )}
              </div>
            </div>

            {/* Citations Section (if available) */}
            {answer?.citations && answer.citations.length > 0 && (() => {
              // Helper function to extract domain from citation
              const extractDomainFromCitation = (citation) => {
                let domain = '';

                try {
                  if (citation.url) {
                    const url = new URL(citation.url);
                    domain = url.hostname.replace(/^www\./, ''); // Remove www prefix

                    // Special handling for Gemini citations
                    if (domain === 'vertexaisearch.cloud.google.com' && citation.title) {
                      // Try to extract domain from title
                      const domainPatterns = [
                        /^(www\.)?([a-z0-9-]+\.)+[a-z]{2,}$/i,  // Direct domain
                        /^([a-z0-9-]+\.)+[a-z]{2,}/i,           // Domain at start
                        /\b((?:www\.)?(?:[a-z0-9-]+\.)+[a-z]{2,})\b/i  // Domain anywhere
                      ];

                      for (const pattern of domainPatterns) {
                        const match = citation.title.match(pattern);
                        if (match) {
                          domain = match[0].replace(/^www\./, '');
                          break;
                        }
                      }

                      // Fallback: use first word/segment of title
                      if (domain === 'vertexaisearch.cloud.google.com') {
                        domain = citation.title.split(/[\s\-|]/)[0].toLowerCase();
                      }
                    }
                  }
                } catch (e) {
                  // If URL parsing fails, try to use title or fall back to URL
                  domain = citation.title || citation.url || 'Unknown source';
                }

                return domain;
              };

              // Process citations to get unique domains
              const uniqueCitations = [];
              const seenDomains = new Set();

              answer.citations.forEach((citation) => {
                const domain = extractDomainFromCitation(citation);

                // Only add if we haven't seen this domain before
                if (domain && !seenDomains.has(domain)) {
                  seenDomains.add(domain);
                  uniqueCitations.push({
                    domain,
                    url: citation.url
                  });
                }
              });

              // Only render if we have unique citations
              return uniqueCitations.length > 0 ? (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-gray-400 dark:text-[#999999]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-[#c4c4c4] uppercase tracking-wider">
                      {t('results.answerSidebar.citations')}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {uniqueCitations.map((citation, index) => (
                      <div key={index} className="bg-gray-50 dark:bg-[#262626] rounded-lg p-3">
                        <a
                          href={citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-300 hover:text-blue-800 hover:underline"
                        >
                          {citation.domain}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}

            {/* Metadata Section */}
            {answer?.executionTime && (
              <div className="border-t dark:border-[#2e2e2e] pt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-[#999999]">{t('results.answerSidebar.status')}:</span>
                    <span className={`ml-2 font-medium ${
                      answer.status === 'completed' ? 'text-green-600 dark:text-green-300' : 'text-gray-800 dark:text-[#f5f5f5]'
                    }`}>
                      {answer.status || 'Unknown'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-[#999999]">{t('results.answerSidebar.executionTime')}:</span>
                    <span className="ml-2 font-medium text-gray-800 dark:text-[#f5f5f5]">
                      {answer.executionTime}ms
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AnswerSidebar;